import { Link } from "react-router";
import { Archive, Globe, Users } from "lucide-react";
import {
  GOAL_DATA_SOURCE_LABEL,
  GOAL_FREQUENCY_LABEL,
  type PartnerGoal,
} from "@colab/shared";
import { Badge } from "@/components/ui/badge";
import { formatRelative } from "@/lib/utils";
import { GoalRowActions } from "./goal-row-actions";

export function GoalsTable({
  rows,
  targetCounts,
  canManage,
}: {
  rows: PartnerGoal[];
  /** id → target_count map sourced from per-goal `useGoalTargets`. */
  targetCounts: Map<string, number>;
  canManage: boolean;
}) {
  return (
    <div className="overflow-hidden rounded-xl border bg-card">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b bg-muted/30 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
            <th className="px-4 py-2.5">Title</th>
            <th className="px-4 py-2.5">Source</th>
            <th className="px-4 py-2.5">Aggregation</th>
            <th className="px-4 py-2.5">Frequency</th>
            <th className="px-4 py-2.5 text-right">Targets</th>
            <th className="px-4 py-2.5 text-right">Last updated</th>
            <th className="w-10 px-2 py-2.5">
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              className="border-b last:border-b-0 hover:bg-muted/30"
            >
              <td className="px-4 py-3">
                <Link
                  to={`/admin/goals/${r.id}`}
                  className="flex items-center gap-2 font-medium text-foreground hover:text-accent"
                >
                  <span className="truncate">{r.title}</span>
                  {r.partner_id ? (
                    <Badge variant="outline" className="gap-1 text-muted-foreground">
                      <Users className="h-3 w-3" /> Partner
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="gap-1 text-muted-foreground">
                      <Globe className="h-3 w-3" /> Workspace
                    </Badge>
                  )}
                  {r.is_archived && (
                    <Badge variant="outline" className="gap-1 text-muted-foreground">
                      <Archive className="h-3 w-3" /> Archived
                    </Badge>
                  )}
                </Link>
                {r.description && (
                  <p className="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
                    {r.description}
                  </p>
                )}
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {GOAL_DATA_SOURCE_LABEL[r.data_source]}
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {r.aggregation_kind === "count"
                  ? "Count"
                  : `Sum of ${r.aggregation_property ?? "—"}`}
              </td>
              <td className="px-4 py-3 text-muted-foreground">
                {GOAL_FREQUENCY_LABEL[r.frequency]}
              </td>
              <td className="px-4 py-3 text-right tabular-nums">
                {targetCounts.get(r.id) ?? 0}
              </td>
              <td className="px-4 py-3 text-right text-muted-foreground">
                {formatRelative(r.updated_at)}
              </td>
              <td className="px-2 py-3 text-right">
                {canManage ? <GoalRowActions goal={r} /> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!canManage && (
        <p className="border-t bg-muted/20 px-4 py-2 text-[11px] text-muted-foreground">
          Read-only — `goals.manage` permission required to create or edit
          goals.
        </p>
      )}
    </div>
  );
}
